import type { AudioService } from '../../../platform/audio';
import { PLATFORM_AUDIO_ROLES } from '../../../platform/config/audio';
import type { MissionEventSource } from '../../../platform/missions';
import type { MissionRuntimeEvent } from '../../../sdk/mission/events';
import type { ThemeManifest } from '../../../sdk/theme';

/**
 * Which mission moments make a sound (Phase 5 Slice 0″).
 *
 * A separate file from the stage on purpose: the stage is three thousand lines about input,
 * cameras and lanes, and "the bed starts when the mission starts, the team cue plays when a shared
 * celebration completes, nothing else makes a noise" is a small, complete claim that deserves to
 * be readable and testable on its own.
 *
 * **Nothing here may throw (S0-R1).** `handle` is called from inside the mission runtime's event
 * callback, beside the code that publishes to the scene bus and sets the round the shell renders.
 * An exception escaping this function does not lose a sound — it aborts the rest of that handler
 * and propagates back into the runtime's own emit, freezing the round exactly where it stood with
 * the help ladder never firing. The service below it is guarded too; this is the belt to that
 * pair of braces, because the cost of being wrong is a five-year-old's mission stopping dead.
 *
 * **What is deliberately silent, and why.** There is no per-round sound, no correct sound, no
 * wrong sound and no timer sound. In a cooperative sitting a per-round cue is a per-child cue — it
 * would tell the room which hero got there first — and a wrong-answer cue is a buzzer, which this
 * product does not have at any volume (the locked non-competitive and no-deduction rules reach the
 * speakers too). The one success sound belongs to the team.
 */

/**
 * The sound the stage owns — and **cannot make at all** (R3-5, R4-5).
 *
 * There is nothing callable here but `dispose`. Round 3 took away `handle` and offered
 * `after(gameplay)` instead, which is better and is still a sound-bearing callable: the *caller*
 * decides what counts as "gameplay", so `after(() => undefined)(event)` could be dropped in ahead
 * of the gameplay effects, or a second time, and every test stayed green.
 *
 * So sound stopped being something the stage *does* with an event. This owner **observes** events
 * the stage has already published, through the relay that fans them out — which also means the
 * S0-R1 isolation is the relay's, applied to every observer by construction, rather than a `try`
 * somebody has to remember to keep. The stage's only sound-bearing act is construction; there is
 * no ordering left to get wrong because there is no call left to place.
 */
export interface StageAudio {
  /** The stage is going away: everything this started stops. */
  dispose(): void;
}

export function createStageAudio(
  audio: AudioService,
  manifest: ThemeManifest,
  events: MissionEventSource,
): StageAudio {
  /** Audio work that is allowed to fail. Failure is silence, never an exception (S0-R1). */
  const quietly = (work: () => void): void => {
    try {
      work();
    } catch {
      // A sound this sitting cannot make is a quiet sitting, never a stopped one.
    }
  };

  // This sitting's theme decides what is playable at all. Loading here — rather than at app
  // start — is what keeps a second game from being able to play the first one's sounds.
  quietly(() => {
    audio.loadLibrary(manifest.audio);
  });

  const started = new Set<string>();
  /**
   * The sounds this stage believes should be *going*, as opposed to ones that happened (R2-3).
   *
   * Only looping sounds are in here. The distinction is the whole of the fix: a bed is a standing
   * state — "the mission is running, so the bed is playing" — while a cue is a moment. A moment
   * nobody could hear has passed, and re-firing it late would be the backlog D-095(4) rules out
   * when it says pre-unlock plays are dropped, not queued.
   */
  const standing = new Set<string>();
  let disposed = false;
  /** Read through the guard: even a property getter is the audio stack from here (S0-R1). */
  let wasUnlocked = false;
  quietly(() => {
    wasUnlocked = audio.unlocked;
  });

  /**
   * Sounds this stage has asked for and not yet made — **intents**, not a backlog (R5-1).
   *
   * Round 4 put sound behind the relay, which isolates an observer that *throws*. It cannot isolate
   * one that is *slow*: `relay.publish` runs inside GameStage's runtime `onEvent`, so every
   * `audio.play` was entered on the mission runtime's own emit stack. A media stack that blocks for
   * three hundred milliseconds blocked the round for three hundred milliseconds, and no `try`
   * anywhere helps with that. Exceptions were the half S0-R1 had closed; time was the half it had
   * not.
   *
   * So the observer records what should be heard and returns immediately. The list is drained once
   * the stack that published the event has gone, in the order the events happened — and it is
   * dropped on disposal rather than played late, because a cue for a stage that has ended is a
   * sound nobody is waiting for (D-095(4)).
   */
  const intents: string[] = [];
  let drainScheduled = false;

  /** Makes one sound. Only ever called with the publishing stack already unwound. */
  const perform = (audioId: string): void => {
    if (disposed) return;
    started.add(audioId);
    if (manifest.audio[audioId]?.loop === true) standing.add(audioId);
    quietly(() => {
      audio.play(audioId);
    });
  };

  const drain = (): void => {
    drainScheduled = false;
    // Shifted one at a time rather than swapped wholesale: `dispose` during a drain must drop what
    // is still unplayed, and a detached copy would carry on making sounds for a stage that has gone.
    while (intents.length > 0) {
      if (disposed) break;
      const audioId = intents.shift();
      if (audioId !== undefined) perform(audioId);
    }
    intents.length = 0;
  };

  const play = (audioId: string): void => {
    if (disposed) return;
    // An ID this theme does not declare is silence, not an error: the service already refuses it,
    // and remembering it here would make `dispose` stop something that never started.
    if (manifest.audio[audioId] === undefined) return;
    intents.push(audioId);
    if (drainScheduled) return;
    drainScheduled = true;
    // A microtask, so the sound is made as soon as the round's own handler is finished with the
    // stack and not one frame later: the deferral is about *separation*, never about delay.
    queueMicrotask(drain);
  };

  /**
   * A sitting that becomes capable of sound part-way through says its standing sounds again.
   *
   * After a reload-and-carry-on the unlock is still in flight when `MISSION_STARTED` arrives —
   * "Carry on" starts `resume()` and navigates without waiting, because S0-R1 forbids a gameplay
   * transition waiting on audio. The service correctly refuses a play it cannot make; without this,
   * nothing ever asked again and the whole resumed mission ran with no bed.
   *
   * This is not a queue at the seam below: nothing accumulates, nothing is replayed late, and the
   * service's drop rule is untouched. It is this stage restating a fact it already holds.
   */
  let unsubscribe: (() => void) | null = null;
  // Subscribing is itself audio work, and this runs while the stage is being built. A service that
  // objects to being watched costs this sitting its bed; it may never cost it the mission.
  quietly(() => {
    unsubscribe = audio.subscribe((state) => {
      const becameUnlocked = state.unlocked && !wasUnlocked;
      wasUnlocked = state.unlocked;
      if (disposed || !becameUnlocked) return;
      for (const audioId of standing) {
        // A loop already looping is already what was asked for; the service treats a repeat as a
        // no-op, so this can only start something that is genuinely silent.
        if (state.playing.includes(audioId)) continue;
        quietly(() => {
          audio.play(audioId);
        });
      }
    });
  });

  /**
   * What this stage's sounds are, per event. Private on purpose: nothing outside this file may
   * hold it, which is what makes "gameplay first, guarded, always" true rather than checked.
   */
  const handle = (event: MissionRuntimeEvent): void => {
    if (disposed) return;

    switch (event.type) {
      case 'MISSION_STARTED':
        play(PLATFORM_AUDIO_ROLES.missionLoop);
        break;
      /**
       * The team's celebration (COOP-06 / AUD-05).
       *
       * `bothRequiredPlayerIds` is on this event and is deliberately not read: the cue is for
       * the moment, and it is identical whether both children joined in, one did, or neither.
       * A sound that varied with who moved would be a ranking made of noise.
       */
      case 'SHARED_MOMENT_COMPLETED':
        play(PLATFORM_AUDIO_ROLES.teamSuccess);
        break;
      default:
        // Everything else — every round, every attempt, every hint, the end of the mission
        // itself — is silent. The bed carries on through the celebration rather than stopping
        // on `MISSION_COMPLETED`; it ends when the stage does.
        break;
    }
  };

  /**
   * Fed by the events the stage has already published (R4-5).
   *
   * The relay's own fan-out isolates every observer, so a sound that throws costs this sitting the
   * sound and nothing else — the same S0-R1 promise the hand-written `try` used to make, made once
   * for everybody instead of once per caller who remembers.
   */
  const unsubscribeEvents = events.subscribe(handle);

  return {
    dispose() {
      if (disposed) return;
      disposed = true;
      // Undelivered intents are dropped, never flushed: a celebration for a stage that has ended is
      // a sound that has passed, and playing it over whatever screen the family went to next is the
      // late-backlog failure D-095(4) rules out.
      intents.length = 0;
      standing.clear();
      quietly(() => {
        unsubscribeEvents();
      });
      quietly(() => {
        unsubscribe?.();
      });
      for (const audioId of started) {
        quietly(() => {
          audio.stop(audioId);
        });
      }
      started.clear();
    },
  };
}
